import React, { useEffect, useMemo, useState } from "react";
import { authAPI } from "../services/api";
import "./AuthRecoveryPage.css";

const MIN_PASSWORD_LENGTH = 8;

const getParamFromUrl = (name) => {
  if (typeof window === "undefined") return "";
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`[?&#]${escapedName}=([^&#]*)`, "i");
  const match = window.location.href.match(regex);
  return match ? decodeURIComponent(match[1].replace(/\+/g, " ")) : "";
};

const cleanRecoveryUrl = () => {
  if (typeof window === "undefined") return;
  const clean = `${window.location.pathname}#/reset-password`;
  window.history.replaceState({}, document.title, clean);
};

const ResetPasswordPage = ({ onResetComplete, onRequestResetLink }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const recoverySession = useMemo(() => {
    const accessToken = getParamFromUrl("access_token");
    const refreshToken = getParamFromUrl("refresh_token");
    const expiresInRaw = Number(getParamFromUrl("expires_in"));
    const tokenType = getParamFromUrl("token_type") || "bearer";
    const type = getParamFromUrl("type");

    return {
      accessToken,
      refreshToken,
      expiresIn: Number.isFinite(expiresInRaw) ? expiresInRaw : 0,
      tokenType,
      type,
    };
  }, []);

  useEffect(() => {
    if (recoverySession.accessToken) {
      cleanRecoveryUrl();
    }
  }, [recoverySession.accessToken]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!recoverySession.accessToken) {
      setError("Reset link is missing or expired. Request a new password reset email.");
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword({
        accessToken: recoverySession.accessToken,
        password,
      });

      setMessage("Password updated. Redirecting...");

      onResetComplete?.(
        recoverySession.refreshToken
          ? {
              accessToken: recoverySession.accessToken,
              refreshToken: recoverySession.refreshToken,
              expiresIn: recoverySession.expiresIn,
              tokenType: recoverySession.tokenType,
              user: null,
            }
          : null,
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error
          || "Unable to update password. Request a new reset link and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const hasRecoveryToken = Boolean(recoverySession.accessToken);

  return (
    <section className="auth-recovery-page">
      <div className="auth-recovery-card">
        <h2>Create New Password</h2>
        <p>
          Enter your new password below. For security, this reset link can only be used for a
          limited time.
        </p>

        {recoverySession.type && recoverySession.type !== "recovery" ? (
          <p className="auth-recovery-footnote">
            Current link type: <strong>{recoverySession.type}</strong>
          </p>
        ) : null}

        {hasRecoveryToken ? (
          <form className="auth-recovery-form" onSubmit={handleSubmit}>
            <label htmlFor="reset-password">
              New Password
              <input
                id="reset-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>

            <label htmlFor="reset-password-confirm">
              Confirm New Password
              <input
                id="reset-password-confirm"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>

            {error ? <p className="auth-recovery-error">{error}</p> : null}
            {message ? <p className="auth-recovery-success">{message}</p> : null}

            <div className="auth-recovery-actions">
              <button type="submit" className="primary-btn" disabled={loading}>
                {loading ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        ) : (
          <div className="auth-recovery-form">
            <p className="auth-recovery-error">
              Reset link is missing or expired. Request a new reset email.
            </p>
            <div className="auth-recovery-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={onRequestResetLink}
              >
                Request New Reset Link
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ResetPasswordPage;
